import axios from 'axios';
import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Film, Camera } from 'lucide-react';
import '../styles/login.css';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import { useParams } from 'react-router-dom';
import { baseUrl } from '../config';



const RECAPTCHA_SITE_KEY = '6Lf2nTEqAAAAAGOj0TIJECaKPGZP4EvGOLpqZSiu';

const ResetPw = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordValidationError, setPasswordValidationError] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const user_id = params.user_id;

  const handleLogin = async (e) => {
    e.preventDefault();
    if ((password === confirmPassword) && (validatePassword(password)) && (captchaValue)) {
      setIsLoading(true);
      const response = await fetch(`${baseUrl}/changePassword`, {
        method : "PUT",
        headers : {
          user_id : user_id,
          password: password
        }
      });
      if (response.ok) {
        // Handle successful upload
      }
      navigate("/login");
    }
    if (!captchaValue) {
      setErrorMessage("Please complete the CAPTCHA.");
      return;
    }
  }

  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*\d)[A-Za-z\d!@#$%^&*]{10,}$/;
    return regex.test(password);
  };

  const handleConfirmPasswordChange = (e) => {
      const newConfirmPassword = e.target.value;
      setConfirmPassword(newConfirmPassword);
      validatePasswordMatch(password, newConfirmPassword);
  };

  const validatePasswordMatch = (password, confirmPassword) => {
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  };    

  const handlePasswordChange = (e) => {
      const newPassword = e.target.value;
      setPassword(newPassword);
      if (!validatePassword(newPassword)) {
        setPasswordValidationError(
          'Password must be at least 10 characters long, include 1 capital letter, 1 special character, and 1 numerical digit.'
        );
      } else {
        setPasswordValidationError('');
      }
      validatePasswordMatch(newPassword, confirmPassword);
  };

  // Generate random film reel positions for background
  const filmReels = [...Array(20)].map((_, i) => ({
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    rotation: Math.random() * 360,
  }));

  return (
    <>
      <SEO
        title="Reset Password"
        description="Enter your new password to reset your ProducerBuddy account credentials."
        url="https://producerbuddy.pilomation.com/reset-pw"
        robots="noindex, nofollow"
      />
      <Nav/>
    <div className="min-h-screen flex items-center justify-center p-8 relative bg-gray-700">
      {/* Animated film reel background */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        {filmReels.map((reel, i) => (
          <div
            key={i}
            className="absolute animate-pulse"
            style={{
              left: reel.left,
              top: reel.top,
              transform: `rotate(${reel.rotation}deg)`
            }}
          >
            <Film className="w-16 h-16 text-cyan-300" />
          </div>
        ))}
      </div>
      <div className="pb-login-container relative max-w-2xl w-full">
        {/* Film strip edges */}
        <div className="absolute -left-4 top-0 bottom-0 w-8 bg-gray-800 flex flex-col justify-between py-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-4 w-full bg-gray-700" />
          ))}
        </div>
        <div className="absolute -right-4 top-0 bottom-0 w-8 bg-gray-800 flex flex-col justify-between py-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-4 w-full bg-gray-700" />
          ))}
        </div>

        <div className="pb-login-card bg-gray-800 p-12 rounded-lg shadow-xl border border-cyan-300/30">
          {/* Logo and title */}
          <div className="text-center mb-12">
            {/* <div className="flex justify-center mb-6">
              <Clapperboard className="w-20 h-20 text-cyan-300" />
            </div> */}
            <h1 className="pb-login-title text-3xl font-bold text-white"> Change Password </h1>
          </div>

          {errorMessage && (
            <div className="pb-error-message bg-red-800 text-red-200 p-4 rounded mb-4 border border-red-400">
              {errorMessage}
            </div>
          )}

          <form className="pb-login-form space-y-6" onSubmit={handleLogin}>
            <div className="pb-form-group">
              <div className="signup-form-group">
                <label className="required" id='passLabel'>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                />
                {passwordValidationError && <p className="signup-password-validation-error">{passwordValidationError}</p>}
              </div>

              <div className="signup-form-group">
                <label className="required" id='confPassLabel'>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  required
                />
                {passwordError && <p className="signup-password-error">{passwordError}</p>}
              </div>
            </div>

            <div className="pb-captcha-container flex justify-center">
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(value) => setCaptchaValue(value)}
                theme="dark"
              />
            </div>

            <button
              type="submit"
              className="pb-login-button bg-cyan-500 hover:bg-cyan-400 flex items-center justify-center gap-3 text-lg py-4 text-white font-semibold"
              disabled={isLoading}
            >
              <Camera className="w-6 h-6" />
              {isLoading ? 'Loading...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
    </>
  );
  }

export default ResetPw;
